import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Alert,
  LinearProgress,
  Tooltip,
  IconButton,
  Divider,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import {
  WalteroCard,
  WalteroButton,
  WalteroStatusChip,
  WalteroTypography,
  WalteroSkeleton
} from './waltero';
import billingService from '../services/billingService';
import ActivateSubscriptionModal from './ActivateSubscriptionModal';
import UpdateQuantityModal from './UpdateQuantityModal';
import CancelSubscriptionModal from './CancelSubscriptionModal';

/**
 * Subscription Management Tab
 *
 * Main view for managing subscriptions
 * Shows available (admin-assigned) and active subscriptions
 */
const SubscriptionManagementTab = ({ organizationId }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableSubscriptions, setAvailableSubscriptions] = useState([]);
  const [activeSubscriptions, setActiveSubscriptions] = useState([]);

  // Modal states
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [updateMode, setUpdateMode] = useState('increase'); // 'increase' or 'decrease'
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  // Load subscriptions
  useEffect(() => {
    if (organizationId) {
      loadSubscriptions();
    } else {
      setLoading(false);
      setAvailableSubscriptions([]);
      setActiveSubscriptions([]);
    }
  }, [organizationId]);

  const loadSubscriptions = async () => {
    if (!organizationId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Load both available and active subscriptions in parallel
      const [availableData, activeData] = await Promise.all([
        billingService.getAvailableSubscriptions(organizationId),
        billingService.getActiveSubscriptions(organizationId)
      ]);

      console.log('Available subscriptions from API:', availableData.availableSubscriptions);
      console.log('Active subscriptions from API:', activeData.activeSubscriptions);

      setAvailableSubscriptions(availableData.availableSubscriptions || []);
      setActiveSubscriptions(activeData.activeSubscriptions || []);
    } catch (err) {
      console.error('Error loading subscriptions:', err);
      setError(err.error || 'Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  // Handle activation
  const handleActivate = (subscription) => {
    setSelectedSubscription(subscription);
    setActivateModalOpen(true);
  };

  // Handle quantity update
  const handleQuantityUpdate = (subscription, mode) => {
    setSelectedSubscription(subscription);
    setUpdateMode(mode);
    setUpdateModalOpen(true);
  };

  // Handle cancellation
  const handleCancel = (subscription) => {
    setSelectedSubscription(subscription);
    setCancelModalOpen(true);
  };

  // Handle reactivation
  const handleReactivate = async (subscription) => {
    try {
      await billingService.reactivateSubscription(organizationId, subscription.id);
      await loadSubscriptions();
    } catch (err) {
      console.error('Error reactivating subscription:', err);
      setError(err.error || 'Failed to reactivate subscription');
    }
  };

  // Cancel scheduled quantity change
  const handleCancelScheduledChange = async (subscription) => {
    try {
      await billingService.cancelScheduledQuantityChange(organizationId, subscription.id);
      await loadSubscriptions();
    } catch (err) {
      console.error('Error cancelling scheduled change:', err);
      setError(err.error || 'Failed to cancel scheduled change');
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate utilization percentage
  const getUtilizationPercentage = (assigned, total) => {
    if (!total || total === 0) return 0;
    return Math.round((assigned / total) * 100);
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'cancelling':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box>
        <Grid container spacing={3}>
          {[1, 2].map((i) => (
            <Grid item xs={12} key={i}>
              <WalteroSkeleton variant="rectangular" height={200} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <WalteroTypography variant="h5" fontWeight="bold">
          Subscription Management
        </WalteroTypography>
        <Tooltip title="Refresh subscriptions">
          <IconButton onClick={loadSubscriptions}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Available Subscriptions (Assigned to organization, pending activation) */}
      {availableSubscriptions.filter(s => s.canActivate).length > 0 && (
        <>
          <WalteroTypography variant="h6" gutterBottom>
            Available Subscriptions
          </WalteroTypography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            These subscriptions have been assigned to your organization and are ready for activation
          </Typography>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {availableSubscriptions
              .filter(s => s.canActivate)
              .map((subscription) => (
                <Grid item xs={12} md={6} key={subscription.id}>
                  <WalteroCard
                    sx={{
                      p: 3,
                      border: `1px dashed ${theme.palette.waltero.primary.coreBlue}`,
                      backgroundColor: theme.palette.waltero.secondary.backgroundApricot + '20'
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                      <Box>
                        <Typography variant="h6">
                          {subscription.name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {subscription.description}
                        </Typography>
                      </Box>
                      <WalteroStatusChip
                        label="PENDING ACTIVATION"
                        color="warning"
                      />
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">
                          Price per sensor
                        </Typography>
                        <Typography variant="h6">
                          ${subscription.pricePerSensorPerMonth}/month
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">
                          Annual billing
                        </Typography>
                        <Typography variant="h6">
                          ${subscription.annualPrice}/year
                        </Typography>
                      </Grid>
                    </Grid>

                    {/* Features */}
                    {subscription.features && subscription.features.length > 0 && (
                      <Box mb={2}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Includes:
                        </Typography>
                        {subscription.features.slice(0, 3).map((feature, idx) => (
                          <Typography key={idx} variant="body2">
                            • {feature}
                          </Typography>
                        ))}
                      </Box>
                    )}

                    <WalteroButton
                      variant="contained"
                      fullWidth
                      startIcon={<CheckCircleIcon />}
                      onClick={() => handleActivate(subscription)}
                    >
                      ACTIVATE SUBSCRIPTION
                    </WalteroButton>
                  </WalteroCard>
                </Grid>
              ))}
          </Grid>
        </>
      )}

      {/* Active Subscriptions */}
      <WalteroTypography variant="h6" gutterBottom>
        Active Subscriptions
      </WalteroTypography>

      {activeSubscriptions.length === 0 ? (
        <WalteroCard sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            No Active Subscriptions
          </Typography>
          <Typography color="textSecondary">
            {availableSubscriptions.filter(s => s.canActivate).length > 0
              ? 'Activate an available subscription above to get started'
              : 'No subscriptions have been assigned to your organization. Please contact your administrator.'}
          </Typography>
        </WalteroCard>
      ) : (
        <Grid container spacing={3}>
          {activeSubscriptions.map((subscription) => {
            const utilizationPercent = getUtilizationPercentage(
              subscription.assignedSensorCount,
              subscription.purchasedSensorQuantity
            );

            return (
              <Grid item xs={12} key={subscription.id}>
                <WalteroCard sx={{ p: 3 }}>
                  {/* Header */}
                  <Box display="flex" justifyContent="space-between" alignItems="start" mb={3}>
                    <Box>
                      <Typography variant="h6">
                        {subscription.subscriptionTypeName}
                      </Typography>
                      <Box display="flex" gap={1} mt={1}>
                        <WalteroStatusChip
                          label={subscription.activationStatus.toUpperCase()}
                          color={getStatusColor(subscription.activationStatus)}
                        />
                        {subscription.scheduledQuantityChange && (
                          <Chip
                            label={`Scheduled: ${subscription.scheduledQuantityChange} sensors`}
                            icon={<ScheduleIcon />}
                            size="small"
                            color="info"
                          />
                        )}
                      </Box>
                    </Box>
                    <Box display="flex" gap={1}>
                      {subscription.activationStatus === 'active' && (
                        <>
                          <Tooltip title="Increase quantity">
                            <IconButton
                              onClick={() => handleQuantityUpdate(subscription, 'increase')}
                              color="primary"
                            >
                              <TrendingUpIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Decrease quantity">
                            <IconButton
                              onClick={() => handleQuantityUpdate(subscription, 'decrease')}
                              color="secondary"
                            >
                              <TrendingDownIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancel subscription">
                            <IconButton
                              onClick={() => handleCancel(subscription)}
                              color="error"
                            >
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {subscription.activationStatus === 'cancelling' && (
                        <WalteroButton
                          size="small"
                          onClick={() => handleReactivate(subscription)}
                        >
                          UNDO CANCELLATION
                        </WalteroButton>
                      )}
                    </Box>
                  </Box>

                  {/* Sensor Usage */}
                  <Box mb={3}>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2" color="textSecondary">
                        Sensor Usage
                      </Typography>
                      <Typography variant="body2">
                        {subscription.assignedSensorCount} / {subscription.purchasedSensorQuantity} assigned
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={utilizationPercent}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: theme.palette.grey[200],
                        '& .MuiLinearProgress-bar': {
                          backgroundColor:
                            utilizationPercent > 90
                              ? theme.palette.error.main
                              : utilizationPercent > 75
                              ? theme.palette.warning.main
                              : theme.palette.success.main
                        }
                      }}
                    />
                    <Box display="flex" justifyContent="space-between" mt={1}>
                      <Typography variant="caption" color="textSecondary">
                        {subscription.availableQuantity} available
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {utilizationPercent}% utilized
                      </Typography>
                    </Box>
                  </Box>

                  {/* Billing Details */}
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="textSecondary">
                        Next Billing Date
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(subscription.currentPeriodEnd)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="textSecondary">
                        Annual Cost
                      </Typography>
                      <Typography variant="body1">
                        ${(subscription.purchasedSensorQuantity * 12).toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="textSecondary">
                        Stripe ID
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {subscription.stripeSubscriptionId?.slice(0, 20)}...
                      </Typography>
                    </Grid>
                  </Grid>

                  {/* Scheduled Changes */}
                  {subscription.scheduledQuantityChange && (
                    <Alert
                      severity="info"
                      sx={{ mt: 2 }}
                      action={
                        <WalteroButton
                          size="small"
                          onClick={() => handleCancelScheduledChange(subscription)}
                        >
                          CANCEL CHANGE
                        </WalteroButton>
                      }
                    >
                      Quantity will change to {subscription.scheduledQuantityChange} sensors on{' '}
                      {formatDate(subscription.scheduledQuantityChangeDate)}
                    </Alert>
                  )}

                  {/* Cancellation Notice */}
                  {subscription.activationStatus === 'cancelling' && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      This subscription will be cancelled on {formatDate(subscription.cancellationDate)}
                    </Alert>
                  )}
                </WalteroCard>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Modals */}
      <ActivateSubscriptionModal
        open={activateModalOpen}
        onClose={() => {
          setActivateModalOpen(false);
          setSelectedSubscription(null);
        }}
        subscription={selectedSubscription}
        organizationId={organizationId}
        onSuccess={() => {
          setActivateModalOpen(false);
          setSelectedSubscription(null);
          loadSubscriptions();
        }}
      />

      <UpdateQuantityModal
        open={updateModalOpen}
        onClose={() => {
          setUpdateModalOpen(false);
          setSelectedSubscription(null);
        }}
        subscription={selectedSubscription}
        organizationId={organizationId}
        mode={updateMode}
        onSuccess={() => {
          setUpdateModalOpen(false);
          setSelectedSubscription(null);
          loadSubscriptions();
        }}
      />

      <CancelSubscriptionModal
        open={cancelModalOpen}
        onClose={() => {
          setCancelModalOpen(false);
          setSelectedSubscription(null);
        }}
        subscription={selectedSubscription}
        organizationId={organizationId}
        onSuccess={() => {
          setCancelModalOpen(false);
          setSelectedSubscription(null);
          loadSubscriptions();
        }}
      />
    </Box>
  );
};

export default SubscriptionManagementTab;